import { createContext } from 'preact'
import { useCallback, useContext, useEffect, useRef, useState } from 'preact/hooks'
import { useWindowEvent } from '../hooks/use-ui-listeners'
import { styled } from '../setup'

// ============================================================================
// Types
// ============================================================================

interface OverlayConfig {
  id: string
  onClose: () => void
  closeOnEscape?: boolean
  closeOnBackdropClick?: boolean
  /** Higher priority overlays close first. Default: registration order */
  priority?: number
}

interface OverlayContextValue {
  /** Register an overlay. Call when overlay opens. Returns unregister function. */
  register: (config: OverlayConfig) => () => void
  /** Check if this overlay is the topmost (for visual stacking) */
  isTopmost: (id: string) => boolean
  /** Get z-index for an overlay based on its stack position */
  getZIndex: (id: string, base?: number) => number
  /** Number of currently open overlays */
  overlayCount: number
}

interface OverlayProviderProps {
  children: preact.ComponentChildren
  /** Base z-index for the backdrop. Overlays stack above this. Default: 1000 */
  baseZIndex?: number
}

// ============================================================================
// Context
// ============================================================================

const OverlayContext = createContext<OverlayContextValue | null>(null)

// ============================================================================
// Styled Components
// ============================================================================

const Backdrop = styled('div')<{ zIndex: number; visible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${(p) => p.zIndex};
  background: transparent;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  pointer-events: ${(p) => (p.visible ? 'auto' : 'none')};
  transition: opacity 150ms ease;
`

// ============================================================================
// Provider Component
// ============================================================================

export function OverlayProvider({ children, baseZIndex = 1000 }: OverlayProviderProps) {
  // Stack of registered overlays (most recent last)
  const [overlays, setOverlays] = useState<OverlayConfig[]>([])
  const overlaysRef = useRef<OverlayConfig[]>([])

  // Keep ref in sync for event handlers
  overlaysRef.current = overlays

  // Register an overlay
  const register = useCallback((config: OverlayConfig) => {
    setOverlays((prev) => {
      // Don't add duplicates
      if (prev.some((o) => o.id === config.id)) {
        return prev.map((o) => (o.id === config.id ? config : o))
      }
      return [...prev, config]
    })

    // Return unregister function
    return () => {
      setOverlays((prev) => prev.filter((o) => o.id !== config.id))
    }
  }, [])

  // Check if overlay is topmost
  const isTopmost = useCallback(
    (id: string) => {
      if (overlays.length === 0) return false
      return overlays[overlays.length - 1].id === id
    },
    [overlays],
  )

  // Get z-index for an overlay
  const getZIndex = useCallback(
    (id: string, base = baseZIndex) => {
      const index = overlays.findIndex((o) => o.id === id)
      if (index === -1) return base + 2 // Default if not found
      // Backdrop is at base, overlays stack above
      return base + 2 + index * 2
    },
    [overlays, baseZIndex],
  )

  // Handle escape key - close topmost overlay that wants escape handling
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Escape') return

    const current = overlaysRef.current
    if (current.length === 0) return

    // Find topmost overlay that wants escape handling
    for (let i = current.length - 1; i >= 0; i--) {
      const overlay = current[i]
      if (overlay.closeOnEscape !== false) {
        e.preventDefault()
        overlay.onClose()
        return
      }
    }
  }, [])

  useWindowEvent('keydown', handleEscape)

  // Handle backdrop click - close topmost overlay that wants backdrop handling
  const handleBackdropClick = useCallback(() => {
    const current = overlaysRef.current
    if (current.length === 0) return

    // Find topmost overlay that wants backdrop click handling
    for (let i = current.length - 1; i >= 0; i--) {
      const overlay = current[i]
      if (overlay.closeOnBackdropClick !== false) {
        overlay.onClose()
        return
      }
    }
  }, [])

  const contextValue: OverlayContextValue = {
    register,
    isTopmost,
    getZIndex,
    overlayCount: overlays.length,
  }

  const hasOverlays = overlays.length > 0

  return (
    <OverlayContext.Provider value={contextValue}>
      {children}
      <Backdrop
        className={hasOverlays ? 'ui-interactive' : undefined}
        zIndex={baseZIndex}
        visible={hasOverlays}
        onClick={handleBackdropClick}
        data-overlay-backdrop
      />
    </OverlayContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

interface UseOverlayOptions {
  /** Unique identifier for this overlay */
  id: string
  /** Whether the overlay is currently open */
  isOpen: boolean
  /** Called when the overlay should close (escape, backdrop click) */
  onClose: () => void
  /** Close when Escape is pressed. Default: true */
  closeOnEscape?: boolean
  /** Close when backdrop is clicked. Default: true */
  closeOnBackdropClick?: boolean
}

interface UseOverlayResult {
  /** Whether this overlay is the topmost in the stack */
  isTopmost: boolean
  /** Z-index to use for this overlay's container */
  zIndex: number
  /** Total number of open overlays */
  overlayCount: number
}

/**
 * Hook for overlay components to register with the overlay system.
 *
 * @example
 * ```tsx
 * function MyModal({ isOpen, onClose }) {
 *   const { zIndex, isTopmost } = useOverlay({
 *     id: 'my-modal',
 *     isOpen,
 *     onClose,
 *   })
 *
 *   if (!isOpen) return null
 *
 *   return (
 *     <ModalContainer style={{ zIndex }}>
 *       <ModalContent>...</ModalContent>
 *     </ModalContainer>
 *   )
 * }
 * ```
 */
export function useOverlay(options: UseOverlayOptions): UseOverlayResult {
  const context = useContext(OverlayContext)

  if (!context) {
    throw new Error('useOverlay must be used within an OverlayProvider')
  }

  const { id, isOpen, onClose, closeOnEscape = true, closeOnBackdropClick = true } = options
  const { register, isTopmost, getZIndex, overlayCount } = context

  // Register/unregister based on isOpen
  useEffect(() => {
    if (!isOpen) return

    const unregister = register({
      id,
      onClose,
      closeOnEscape,
      closeOnBackdropClick,
    })

    return unregister
  }, [isOpen, id, onClose, closeOnEscape, closeOnBackdropClick, register])

  return {
    isTopmost: isTopmost(id),
    zIndex: getZIndex(id),
    overlayCount,
  }
}

// ============================================================================
// Utility Hook for simple open/close state
// ============================================================================

interface UseOverlayStateResult {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

/**
 * Simple state management for overlay open/close.
 * Use this with useOverlay for complete overlay management.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const modalState = useOverlayState()
 *   const { zIndex } = useOverlay({
 *     id: 'my-modal',
 *     isOpen: modalState.isOpen,
 *     onClose: modalState.close,
 *   })
 *
 *   return (
 *     <>
 *       <button onClick={modalState.open}>Open Modal</button>
 *       {modalState.isOpen && <Modal zIndex={zIndex} onClose={modalState.close} />}
 *     </>
 *   )
 * }
 * ```
 */
export function useOverlayState(initialOpen = false): UseOverlayStateResult {
  const [isOpen, setIsOpen] = useState(initialOpen)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((v) => !v), [])

  return { isOpen, open, close, toggle }
}
